import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Save, Users, Loader2, Check } from 'lucide-react'
import { getEnlevementConfig, updateEnlevementConfig } from '../../api/enlevement'

const label = 'block text-xs font-semibold uppercase tracking-wide text-ink/55 mb-1.5'
const champ = 'w-full rounded-xl border border-navy/15 bg-white px-3.5 py-2.5 text-sm outline-none focus:border-orange'

export function AdminEnlevementConfig() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['enlevement-config'], queryFn: getEnlevementConfig })
  const [vals, setVals] = useState<Record<string, string>>({})
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (data?.config) {
      setVals({
        DateDebut: data.config.DateDebut || '',
        DateFin: data.config.DateFin || '',
        MaxCompteur: data.config.MaxCompteur || '3',
        MaxTentativesIP: data.config.MaxTentativesIP || '5',
      })
    }
  }, [data])

  const mut = useMutation({
    mutationFn: () => updateEnlevementConfig(vals),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enlevement-config'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    },
  })

  if (isLoading) {
    return <div className="grid place-items-center py-20 text-ink/50"><Loader2 className="h-6 w-6 animate-spin" /></div>
  }

  const compteur = data?.config.CompteurDemandes ?? '0'

  return (
    <div className="max-w-xl">
      <p className="text-sm text-ink/60">
        Le formulaire public de demande n'est accessible qu'aux clients possédant un <strong>code personnel</strong>,
        et uniquement entre les deux dates ci-dessous.
      </p>

      <div className="mt-5 flex flex-wrap gap-4">
        <div className="flex items-center gap-2 rounded-xl bg-sand px-4 py-2.5 text-sm">
          <Users className="h-4 w-4 text-navy" />
          <span className="font-semibold text-navy">{data?.nb_clients ?? 0}</span> clients avec code
        </div>
        <div className="flex items-center gap-2 rounded-xl bg-sand px-4 py-2.5 text-sm">
          Dernière demande n° <span className="font-semibold text-navy">{compteur}</span>
        </div>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div>
          <label className={label}>Ouverture (date de début)</label>
          <input type="date" value={vals.DateDebut || ''} onChange={(e) => setVals({ ...vals, DateDebut: e.target.value })} className={champ} />
        </div>
        <div>
          <label className={label}>Fermeture (date de fin)</label>
          <input type="date" value={vals.DateFin || ''} onChange={(e) => setVals({ ...vals, DateFin: e.target.value })} className={champ} />
        </div>
        <div>
          <label className={label}>Utilisations max par code</label>
          <input type="number" min={1} value={vals.MaxCompteur || ''} onChange={(e) => setVals({ ...vals, MaxCompteur: e.target.value })} className={champ} />
          <p className="mt-1 text-xs text-ink/45">Nombre de demandes qu'un même code peut envoyer sur la campagne. Ouvrir le formulaire sans rien envoyer ne compte pas.</p>
        </div>
        <div>
          <label className={label}>Tentatives max / 1 h (anti-abus)</label>
          <input type="number" min={1} value={vals.MaxTentativesIP || ''} onChange={(e) => setVals({ ...vals, MaxTentativesIP: e.target.value })} className={champ} />
          <p className="mt-1 text-xs text-ink/45">Nombre de saisies de code autorisées par visiteur sur 1 heure.</p>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <button
          onClick={() => mut.mutate()}
          disabled={mut.isPending}
          className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-dk disabled:opacity-50"
        >
          {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
        </button>
        {saved && <span className="inline-flex items-center gap-1 text-sm font-medium text-green-600"><Check className="h-4 w-4" /> Enregistré</span>}
      </div>
    </div>
  )
}
